// The screen an administrator who has not enrolled a second factor is sent to.
//
// It is not a nag banner, and that is the point. The server answers
// `403 totp_enrolment_required` to every route except the handful needed to
// enrol or sign out (middleware/authenticate.ts), so an admin who has not done
// this genuinely cannot use the account. A dismissible prompt would be a lie
// about what the app will do.
//
// It reuses SecurityPage's own panel rather than a second enrolment flow. Two
// enrolment screens is two places for the wording to drift, and this one would
// be the one nobody looks at again after launch.
import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { ApiError, api, type SecurityStatus } from '../api';
import { AuthCard, Banner } from '../ui';
import { TwoFactorPanel } from './SecurityPage';

export function EnrolTwoFactorPage() {
  const [status, setStatus] = useState<SecurityStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const check = useCallback(async () => {
    // /api/me/security is one of the few routes the gate lets through, which
    // is what makes this page reachable at all.
    //
    // A failure here is worse than it is anywhere else in the app. Every other
    // route answers 403 while an admin is ungated, so a dropped connection or a
    // server that was restarting left this page rendering nothing, on the one
    // screen there is no navigating away from — an account that looks broken
    // rather than one waiting on a phone.
    try {
      setStatus(await api.security());
      setError(null);
    } catch (err: unknown) {
      setError(
        err instanceof ApiError ? err.message : 'Could not reach Dayboard. Try again in a moment.'
      );
    }
  }, []);

  useEffect(() => {
    void check();
  }, [check]);

  // Somebody who has finished, or who never needed this, is sent on rather
  // than left staring at a page telling them to do something already done.
  if (done || (status && (status.totpEnabled || !status.totpRequired))) {
    return <Navigate to="/app" replace />;
  }

  if (!status) {
    if (!error) return null;
    return (
      <AuthCard title="Set up two-factor authentication">
        <div className="banner-error mb-4" role="alert">
          {error}
        </div>
        <button className="btn-primary" onClick={() => void check()}>
          Try again
        </button>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Set up two-factor authentication">
      <Banner kind="error">
        Your account administers a company on Dayboard, so it needs more than a password.
        Nothing else will open until this is done.
      </Banner>
      <p className="mb-4 text-sm text-gray-500">
        You will need an authenticator app on your phone: Google Authenticator, Microsoft
        Authenticator and 1Password all work, and any of them takes about a minute to
        install.
      </p>
      <TwoFactorPanel status={status} onChanged={() => setDone(true)} />
    </AuthCard>
  );
}
